import BalanceIcon from "@mui/icons-material/Balance";
import { Box } from "@mui/system";
import { MoveToInbox } from "@mui/icons-material";
import MediationIcon from "@mui/icons-material/Mediation";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ShieldIcon from "@mui/icons-material/Shield";
import EditIcon from "@mui/icons-material/Edit";
import StageOne from "./StageOne/StageOne";
import StageTwo from "./StageTwo/StageTwo";
import StageThree from "./StageThree/StageThree";
import StageFour from "./StageFour/StageFour";
import StageFive from "./StageFive/StageFive";
import StageSix from "./StageSix/StageSix";
import LineageAccordionList from "./LineageAccordionList";
import StageNotApplicable from "./StageNotApplicable";
import { useSelector } from "react-redux";
import {
  getDeepViewFieldKey,
  getDeepViewLineageEntry,
  getDeepViewValuesEntry,
  isGapRuleBaPrefix,
} from "../deepViewFieldMap";
import { toFirstOfMonth } from "../deepViewDates";
import { areAmountsEqual } from "../deepViewNumbers";

// Stages a field never reaches — its body is replaced by a "not applicable"
// notice. Fields not listed here (e.g. total_income) pass through every stage.
const SKIPPED_STAGES_BY_FIELD: Record<string, number[]> = {
  registrations: [3, 5, 6],
  ftd: [5, 6],
  qftd: [5, 6],
  cpa_income: [6],
  revshare_income: [6],
  net_revenue: [6],
};

// Stage 6 badge, keyed by the last segment of `stage6_template_key`
// (e.g. "stage6.offset.no_offset_this_period" → "no_offset_this_period")
const STAGE_SIX_BADGES: Record<
  string,
  { label: string; color: "neutral" | "success" | "gapRule" }
> = {
  not_applicable_field: { label: "n/a", color: "neutral" },
  no_policy: { label: "No Policy", color: "success" },
  no_offset_this_period: { label: "No Offset", color: "success" },
  fenced_cpa: { label: "Fenced CPA Applied", color: "gapRule" },
  negative_offset: { label: "NCO Applied", color: "gapRule" },
};

const FALLBACK_STAGE_SIX_BADGE = STAGE_SIX_BADGES.not_applicable_field;

const formatOffsetValue = (value: any) => {
  const numeric = Number(value);
  if (value === null || value === undefined || isNaN(numeric)) return "-";
  return `${numeric < 0 ? "-" : ""}$${Math.abs(numeric).toLocaleString(
    "en-US",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  )}`;
};

interface Props {
  /** API key of the column the user clicked, e.g. "total_income_usd" */
  columnKey?: string;
  /** Label of the column the user clicked, e.g. "Total Income (USD)" */
  columnLabel?: string;
}

const Lineage = ({ columnKey, columnLabel }: Props) => {
  const deepViewData = useSelector(
    (state: any) => state?.deepView?.deepViewData,
  );
  const lineageEntry = getDeepViewLineageEntry(deepViewData, columnKey);

  const fieldKey = getDeepViewFieldKey(columnKey);

  // Clicks never pass through a fetcher — they come from internal tracking
  const showFetcherStage = fieldKey !== "clicks";

  const skippedStages = SKIPPED_STAGES_BY_FIELD[fieldKey ?? ""] ?? [];
  const isStageSkipped = (stage: number) => skippedStages.includes(stage);
  const notApplicableNotice = (
    <StageNotApplicable fieldLabel={lineageEntry?.field_label ?? columnLabel} />
  );

  const fetcherSubtitle = [
    lineageEntry?.fetcher_interface_name,
    lineageEntry?.collection_type_name,
    lineageEntry?.collection_method_name,
  ]
    .filter(Boolean)
    .join(" · ");

  // Both statuses carry a qualifier, e.g. "collected_by_fetcher" /
  // "mapped_in_unifier", so match on the keyword rather than the whole value
  const isCollected = String(lineageEntry?.fetcher_status ?? "")
    .toLowerCase()
    .includes("collected");
  const isMapped = String(lineageEntry?.unifier_status ?? "")
    .toLowerCase()
    .includes("mapped");

  const isGapRule = isGapRuleBaPrefix(lineageEntry?.ba_prefix);

  const valuesEntry = getDeepViewValuesEntry(deepViewData, columnKey);

  const adjustmentCount = valuesEntry?.adjustments?.length ?? 0;

  const beforeAdj = valuesEntry?.fmt_before_adj_usd;
  const afterAdj = valuesEntry?.fmt_after_adj_usd;
  const isAdjNotApplicable = beforeAdj === null || beforeAdj === undefined;
  const isAdjClean =
    !isAdjNotApplicable && areAmountsEqual(beforeAdj, afterAdj);
  const adjustmentStatus = isAdjNotApplicable
    ? "n/a"
    : isAdjClean
      ? "Clean"
      : `${adjustmentCount} Adjustment(s)`;
  const adjustmentStatusColor = isAdjNotApplicable
    ? "neutral"
    : isAdjClean
      ? "success"
      : "warning";
  const adjustmentSubtitle =
    adjustmentCount === 0
      ? "No Manual Adjustment needed"
      : `${adjustmentCount} manual adjustment(s) applied by BD team · after fraud correction`;

  // Stage 6 — the offset policy this brand is on, if any
  const offsetType = deepViewData?.period?.offset_type;
  const hasOffsetPolicy =
    offsetType === "negative_offset" || offsetType === "fenced_cpa";
  const stage6TemplateKey =
    String(valuesEntry?.stage6_template_key ?? "").split(".").pop() ?? "";
  const stage6Badge =
    STAGE_SIX_BADGES[stage6TemplateKey] ?? FALLBACK_STAGE_SIX_BADGE;

  const offsetSubtitle = hasOffsetPolicy
    ? `${formatOffsetValue(deepViewData?.period?.offset_value)} → written to ${toFirstOfMonth(deepViewData?.period?.fmt_offset_activity_month_year) ?? "-"}`
    : "No Fenced CPA or NCO policy for this brand";

  const hasFraud = Number(deepViewData?.period?.fraud_event_count ?? 0) > 0;
  const fraudSubtitle = hasFraud
    ? `${deepViewData?.fraud_users?.length ?? 0} fraud user(s) removed · values corrected`
    : "No fraud users flagged · values unchanged";

  const isFraudClean = areAmountsEqual(
    valuesEntry?.fmt_before_fraud_usd,
    valuesEntry?.fmt_after_fraud_usd,
  );

  const businessAdviserSubtitle = isGapRule
    ? [
        lineageEntry?.gap_rule_title,
        lineageEntry?.dep_field_label
          ? `inputs: ${lineageEntry.dep_field_label}`
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "Value passed through directly from unifier – no gap rule for this brand";

  const unifierSubtitle = [
    lineageEntry?.source_field_expression && lineageEntry?.field_name
      ? `${lineageEntry.source_field_expression} → ${lineageEntry.field_name}`
      : null,
    lineageEntry?.calculation_type_name,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <Box p={2.1}>
        {showFetcherStage && (
          <LineageAccordionList
            title="STAGE 1 - FETCHER"
            subtitle={fetcherSubtitle}
            icon={<MoveToInbox sx={{ fontSize: 20 }} />}
            status={isCollected ? "Collected" : "Not Collected"}
            statusColor={isCollected ? "success" : "warning"}
          >
            <StageOne columnKey={columnKey} />
          </LineageAccordionList>
        )}

        <LineageAccordionList
          title="STAGE 2 - UNIFIER"
          subtitle={unifierSubtitle}
          icon={<MediationIcon sx={{ fontSize: 20 }} />}
          status={isMapped ? "Mapped" : "Not Mapped"}
          statusColor={isMapped ? "success" : "warning"}
        >
          <StageTwo columnKey={columnKey} />
        </LineageAccordionList>

        <LineageAccordionList
          title="STAGE 3 - BUSINESS ADVISOR"
          subtitle={businessAdviserSubtitle}
          icon={<TuneOutlinedIcon sx={{ fontSize: 20 }} />}
          status={isGapRule ? "Gap Rule" : "Passthrough"}
          statusColor={isGapRule ? "gapRule" : "success"}
        >
          {isStageSkipped(3) ? (
            notApplicableNotice
          ) : (
            <StageThree columnKey={columnKey} />
          )}
        </LineageAccordionList>

        <LineageAccordionList
          title="STAGE 4 - FRAUD CORRECTION"
          subtitle={fraudSubtitle}
          icon={<ShieldIcon sx={{ fontSize: 20 }} />}
          status={isFraudClean ? "Clean" : "Fraud Detected"}
          statusColor={isFraudClean ? "success" : "error"}
        >
          <StageFour columnKey={columnKey} columnLabel={columnLabel} />
        </LineageAccordionList>

        <LineageAccordionList
          title="STAGE 5 - MANUAL ADJUSTMENTS"
          subtitle={adjustmentSubtitle}
          icon={<EditIcon sx={{ fontSize: 20 }} />}
          status={adjustmentStatus}
          statusColor={adjustmentStatusColor}
        >
          {isStageSkipped(5) ? (
            notApplicableNotice
          ) : (
            <StageFive columnKey={columnKey} columnLabel={columnLabel} />
          )}
        </LineageAccordionList>

        <LineageAccordionList
          title="STAGE 6 -POLICY OFFSET"
          subtitle={offsetSubtitle}
          icon={<BalanceIcon sx={{ fontSize: 20 }} />}
          status={stage6Badge.label}
          statusColor={stage6Badge.color}
        >
          {isStageSkipped(6) ? (
            notApplicableNotice
          ) : (
            <StageSix columnKey={columnKey} />
          )}
        </LineageAccordionList>
      </Box>
    </>
  );
};

export default Lineage;
